import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, ExternalLink, RotateCw } from 'lucide-react';
import { getRides, getRideBookings } from '../services/ridesMonitorService';
import { personPath } from '../components/statistics/StatisticsPage';
import { formatDateTime, formatDecimal } from '../utils/period';
import './Users.css';
import '../pages/Invitations.css';
import './SearchHistory.css';
import './RidesMonitor.css';

type Phase = 'upcoming' | 'in_progress' | 'finished' | 'cancelled';

interface Ride {
  id: number;
  link: string;
  status: string;
  phase: Phase;
  createdAt: string;
  cancelledAt: string | null;
  cancellationReason: string | null;
  departureDate: string;
  departureTime: string | null;
  flexMinutes: number | null;
  durationMinutes: number | null;
  distanceKm: number | null;
  origin: string;
  destination: string;
  driver: { id: number; fullName: string | null; email: string | null; campus: string | null };
  seats: { offered: number; booked: number; left: number };
  price: number | null;
  collected: number | null;
  luggageAllowed: boolean;
  genderPreference: string;
  stopsAllowed: boolean;
  requests: {
    total: number;
    people: number;
    pending: number;
    confirmed: number;
    declined: number;
    cancelled: number;
    withdrawn: number;
    removed: number;
    expired: number;
    responseMinutes: number | null;
  };
  messages: number;
  captureAlerts: number;
  searchAppearances: number;
  ratings: { count: number; average: number | null };
  reports: { total: number; noShows: number };
}

interface Booking {
  id: number;
  status: string;
  requestedAt: string;
  respondedAt: string | null;
  cancelledAt: string | null;
  cancelledBy: string | null;
  cancellationReason: string | null;
  price: number | null;
  detourMinutes: number | null;
  rebookCount: number;
  passenger: { id: number; fullName: string | null; email: string | null };
  pickup: string | null;
  dropoff: string | null;
  messages: number;
}

interface Expanded {
  loading: boolean;
  error: string | null;
  bookings: Booking[];
}

const REFRESH_MS = 60_000;

const PHASE_TABS: { key: string; label: string; phases: Phase[] | null }[] = [
  { key: 'active', label: 'Active', phases: ['in_progress', 'upcoming', 'finished'] },
  { key: 'live', label: 'Not yet finished', phases: ['in_progress', 'upcoming'] },
  { key: 'in_progress', label: 'In progress', phases: ['in_progress'] },
  { key: 'upcoming', label: 'Upcoming', phases: ['upcoming'] },
  { key: 'finished', label: 'Finished', phases: ['finished'] },
  { key: 'cancelled', label: 'Cancelled', phases: ['cancelled'] },
  { key: 'all', label: 'All', phases: null },
];

const PHASE_LABELS: Record<Phase, string> = {
  in_progress: 'In progress',
  upcoming: 'Upcoming',
  finished: 'Finished',
  cancelled: 'Cancelled',
};

const BOOKING_STATUS_LABELS: Record<string, string> = {
  pending: 'Waiting for the driver',
  confirmed: 'Confirmed',
  completed: 'Completed',
  rejected: 'Declined',
  cancelled: 'Cancelled',
  'auto-cancelled': 'Auto-cancelled',
  expired: 'Expired',
};

const errorText = (err: any, fallback: string) => err?.response?.data?.message || fallback;

/** Minutes between two naive 'YYYY-MM-DD HH:MM[:SS]' wall times (b − a). */
function minutesBetween(a: string, b: string): number {
  const parse = (s: string) => {
    const [d, t = '00:00:00'] = s.split(' ');
    const [y, m, day] = d.split('-').map(Number);
    const [hh, mm] = t.split(':').map(Number);
    return Date.UTC(y, m - 1, day, hh, mm);
  };
  return Math.round((parse(b) - parse(a)) / 60_000);
}

function durationText(minutes: number): string {
  const abs = Math.abs(minutes);
  if (abs < 60) return `${abs} min`;
  if (abs < 48 * 60) return `${formatDecimal(abs / 60)} h`;
  return `${Math.round(abs / 1440)} days`;
}

/** "in 3 h" before departure, "for 20 min more" while in progress, "2 days ago" after. */
function phaseHint(ride: Ride, now: string | null): string | null {
  if (!now || !ride.departureTime || ride.phase === 'cancelled') return null;
  const departs = `${ride.departureDate} ${ride.departureTime}`;
  const toDeparture = minutesBetween(now, departs);
  if (ride.phase === 'upcoming') return `in ${durationText(toDeparture)}`;
  const toArrival = toDeparture + (ride.durationMinutes || 0);
  if (ride.phase === 'in_progress') return `${durationText(toArrival)} to go`;
  return `${durationText(toArrival)} ago`;
}

function dayText(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

const priceText = (price: number | null) => (price === null ? '—' : price === 0 ? 'Free' : `₪${formatDecimal(price)}`);
const count = (n: number) => (n > 0 ? n : <span className="rm-zero">0</span>);

/**
 * Rides: every ride on the platform, one row each, for following each use closely
 * — where it is in its life, who asked for a seat and what happened, and every
 * other signal we have on it. Refreshes itself every minute.
 */
const RidesMonitor = () => {
  const [rides, setRides] = useState<Ride[]>([]);
  const [phaseCounts, setPhaseCounts] = useState<Record<string, number>>({});
  const [now, setNow] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);

  const [page, setPage] = useState(1);
  const limit = 50;
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });

  const [phase, setPhase] = useState('active');
  const [driverInput, setDriverInput] = useState('');
  const [originInput, setOriginInput] = useState('');
  const [destinationInput, setDestinationInput] = useState('');
  const [text, setText] = useState({ driver: '', origin: '', destination: '' });
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const [expanded, setExpanded] = useState<Record<number, Expanded>>({});
  const expandedRef = useRef(expanded);
  expandedRef.current = expanded;

  useEffect(() => {
    const timer = setTimeout(() => {
      const next = { driver: driverInput.trim(), origin: originInput.trim(), destination: destinationInput.trim() };
      if (next.driver === text.driver && next.origin === text.origin && next.destination === text.destination) return;
      setText(next);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driverInput, originInput, destinationInput]);

  const loadBookings = useCallback(async (rideId: number) => {
    try {
      const response = await getRideBookings(rideId);
      setExpanded((prev) =>
        prev[rideId] ? { ...prev, [rideId]: { loading: false, error: null, bookings: response.data.bookings || [] } } : prev
      );
    } catch (err) {
      setExpanded((prev) =>
        prev[rideId]
          ? { ...prev, [rideId]: { loading: false, error: errorText(err, 'Failed to load seat requests'), bookings: [] } }
          : prev
      );
    }
  }, []);

  /** quiet = a background refresh: keep what's on screen, refresh open rows too. */
  const load = useCallback(
    async (quiet = false) => {
      try {
        if (quiet) setRefreshing(true);
        else setLoading(true);
        const params: Record<string, string | number> = { page, limit, phase };
        if (text.driver) params.driver = text.driver;
        if (text.origin) params.origin = text.origin;
        if (text.destination) params.destination = text.destination;
        if (from) params.from = from;
        if (to) params.to = to;

        const response = await getRides(params);
        setRides(response.data.rides || []);
        setPhaseCounts(response.data.phaseCounts || {});
        setNow(response.data.now || null);
        setPagination(response.data.pagination || { total: 0, pages: 1 });
        setUpdatedAt(new Date());
        setError(null);
        if (quiet) for (const id of Object.keys(expandedRef.current)) loadBookings(Number(id));
        else setExpanded({});
      } catch (err) {
        setError(errorText(err, 'Failed to load rides'));
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [page, phase, text, from, to, loadBookings]
  );

  useEffect(() => {
    load();
  }, [load]);

  // Close following: refresh every minute while the tab is visible.
  useEffect(() => {
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') load(true);
    }, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  const resetPage = <T,>(setter: (value: T) => void) => (value: T) => {
    setter(value);
    setPage(1);
  };

  const toggle = (ride: Ride) => {
    if (expanded[ride.id]) {
      setExpanded(({ [ride.id]: _closed, ...rest }) => rest);
      return;
    }
    setExpanded((prev) => ({ ...prev, [ride.id]: { loading: true, error: null, bookings: [] } }));
    loadBookings(ride.id);
  };

  const clearFilters = () => {
    setDriverInput('');
    setOriginInput('');
    setDestinationInput('');
    setFrom('');
    setTo('');
    setPage(1);
  };
  const hasFilters = driverInput || originInput || destinationInput || from || to;

  const tabCount = (phases: Phase[] | null) =>
    (phases || (['in_progress', 'upcoming', 'finished', 'cancelled'] as Phase[])).reduce(
      (n, p) => n + (phaseCounts[p] || 0),
      0
    );

  return (
    <div className="invitations-page rides-monitor">
      <div className="invitations-header">
        <div>
          <h1>Rides</h1>
          <p className="subtitle">
            Every ride, one row each: where it is in its life, who asked for a seat and what happened, and everything
            else we know about it. Times are Israel time; staff accounts are left out.
          </p>
        </div>
        <div className="rm-refresh">
          <span className="member-email">
            {updatedAt ? `Updated ${updatedAt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}` : ''}
            {' · refreshes every minute'}
          </span>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => load(true)} disabled={refreshing}>
            <RotateCw size={14} className={refreshing ? 'rm-spin' : undefined} /> Refresh
          </button>
        </div>
      </div>

      <div className="rm-tabs" role="tablist">
        {PHASE_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={phase === tab.key}
            className={phase === tab.key ? 'rm-tab active' : 'rm-tab'}
            onClick={() => resetPage(setPhase)(tab.key)}
          >
            {tab.label} <span className="rm-tab-count">{tabCount(tab.phases)}</span>
          </button>
        ))}
      </div>

      {error && (
        <div style={{ marginBottom: '20px' }}>
          <div className="alert alert-error">{error}</div>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <div className="filters">
            <input
              type="text"
              placeholder="Driver: name, email or #id"
              value={driverInput}
              onChange={(e) => setDriverInput(e.target.value)}
              className="search-input"
            />
            <input
              type="text"
              placeholder="From (any language)"
              value={originInput}
              onChange={(e) => setOriginInput(e.target.value)}
              className="search-input"
            />
            <input
              type="text"
              placeholder="To (any language)"
              value={destinationInput}
              onChange={(e) => setDestinationInput(e.target.value)}
              className="search-input"
            />
            <label className="filter-date">
              <span>Departs from</span>
              <input
                type="date"
                value={from}
                max={to || undefined}
                onChange={(e) => resetPage(setFrom)(e.target.value)}
                className="filter-select"
              />
            </label>
            <label className="filter-date">
              <span>to</span>
              <input
                type="date"
                value={to}
                min={from || undefined}
                onChange={(e) => resetPage(setTo)(e.target.value)}
                className="filter-select"
              />
            </label>
            {hasFilters && (
              <button type="button" className="btn btn-secondary btn-sm" onClick={clearFilters}>
                Clear filters
              </button>
            )}
          </div>
        </div>

        {loading && <div className="loading">Loading rides...</div>}

        {!loading && !error && rides.length === 0 && (
          <div className="empty-state">
            <p>No rides here.</p>
          </div>
        )}

        {!loading && rides.length > 0 && (
          <>
            <div className="table-container">
              <table className="invitations-table rm-table">
                <thead>
                  <tr>
                    <th aria-label="Expand" />
                    <th>Ride</th>
                    <th>Phase</th>
                    <th>Departure</th>
                    <th className="rm-route">Route</th>
                    <th>Driver</th>
                    <th title="Seats taken by confirmed riders / seats offered">Seats</th>
                    <th title="Seat requests (distinct riders)">Requests</th>
                    <th>Confirmed</th>
                    <th title="Waiting for the driver to answer">Pending</th>
                    <th>Declined</th>
                    <th title="Cancelled requests: by the rider (withdrew) or the driver (removed them)">Cancelled</th>
                    <th title="Expired or auto-cancelled without an answer">Expired</th>
                    <th title="Average time the driver took to answer">Answer time</th>
                    <th title="Price per seat / total for confirmed seats">Price</th>
                    <th title="Chat messages on the ride">Chat</th>
                    <th title="Times the ride appeared in someone's search results (searches are kept 10 days)">
                      In searches
                    </th>
                    <th title="Ride Capture alerts sent about this ride">Capture alerts</th>
                    <th>Rating</th>
                    <th title="Reports filed about the ride (no-shows)">Reports</th>
                    <th>Posted</th>
                  </tr>
                </thead>
                <tbody>
                  {rides.map((ride) => {
                    const open = expanded[ride.id];
                    const hint = phaseHint(ride, now);
                    const r = ride.requests;
                    return (
                      <Fragment key={ride.id}>
                        <tr className={`rm-row rm-${ride.phase}${open ? ' open' : ''}`}>
                          <td>
                            <button
                              type="button"
                              className="expand-toggle"
                              onClick={() => toggle(ride)}
                              aria-expanded={Boolean(open)}
                              aria-label={open ? 'Hide details' : 'Show details and seat requests'}
                            >
                              {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                            </button>
                          </td>
                          <td className="numeric">
                            <a href={ride.link} target="_blank" rel="noopener noreferrer" className="open-ride-link">
                              #{ride.id} <ExternalLink size={12} />
                            </a>
                          </td>
                          <td>
                            <span className={`rm-phase rm-phase-${ride.phase}`}>{PHASE_LABELS[ride.phase]}</span>
                            {hint && <div className="member-email">{hint}</div>}
                          </td>
                          <td className="rm-nowrap">
                            <div>{dayText(ride.departureDate)}</div>
                            <div className="member-email">
                              {ride.departureTime || '—'}
                              {ride.flexMinutes ? ` (+${ride.flexMinutes} min flex)` : ''}
                            </div>
                          </td>
                          <td className="rm-route">
                            <div>
                              {ride.origin} → {ride.destination}
                            </div>
                            <div className="member-email">
                              {ride.durationMinutes ? durationText(ride.durationMinutes) : ''}
                              {ride.distanceKm ? ` · ${formatDecimal(ride.distanceKm)} km` : ''}
                            </div>
                          </td>
                          <td>
                            <Link to={personPath(ride.driver.id)}>{ride.driver.fullName || `#${ride.driver.id}`}</Link>
                            {ride.driver.email && <div className="member-email">{ride.driver.email}</div>}
                          </td>
                          <td className="numeric">
                            {ride.seats.booked} / {ride.seats.offered}
                            <div className="member-email">{ride.seats.left} left</div>
                          </td>
                          <td className="numeric">
                            {count(r.total)}
                            {r.total > 0 && <div className="member-email">{r.people} riders</div>}
                          </td>
                          <td className="numeric">{count(r.confirmed)}</td>
                          <td className={`numeric${r.pending > 0 && ride.phase === 'upcoming' ? ' rm-attention' : ''}`}>
                            {count(r.pending)}
                          </td>
                          <td className="numeric">{count(r.declined)}</td>
                          <td className="numeric" title={`${r.withdrawn} by the rider, ${r.removed} by the driver`}>
                            {count(r.cancelled)}
                            {r.cancelled > 0 && (
                              <div className="member-email">
                                {r.withdrawn} rider · {r.removed} driver
                              </div>
                            )}
                          </td>
                          <td className="numeric">{count(r.expired)}</td>
                          <td className="numeric">{r.responseMinutes === null ? '—' : durationText(Math.round(r.responseMinutes))}</td>
                          <td className="numeric">
                            {priceText(ride.price)}
                            {ride.collected ? <div className="member-email">₪{formatDecimal(ride.collected)} total</div> : null}
                          </td>
                          <td className="numeric">{count(ride.messages)}</td>
                          <td className="numeric">{count(ride.searchAppearances)}</td>
                          <td className="numeric">{count(ride.captureAlerts)}</td>
                          <td className="numeric">
                            {ride.ratings.count > 0 ? `${formatDecimal(ride.ratings.average)} (${ride.ratings.count})` : '—'}
                          </td>
                          <td className={`numeric${ride.reports.total > 0 ? ' rm-attention' : ''}`}>
                            {count(ride.reports.total)}
                            {ride.reports.noShows > 0 && <div className="member-email">{ride.reports.noShows} no-show</div>}
                          </td>
                          <td className="rm-nowrap">{formatDateTime(ride.createdAt)}</td>
                        </tr>
                        {open && (
                          <tr className="search-rides-row">
                            <td colSpan={21}>
                              <div className="rm-details">
                                <span>
                                  <strong>Status:</strong> {ride.status}
                                </span>
                                <span>
                                  <strong>Luggage:</strong> {ride.luggageAllowed ? 'allowed' : 'no'}
                                </span>
                                <span>
                                  <strong>Stops on the way:</strong> {ride.stopsAllowed ? 'yes' : 'no'}
                                </span>
                                <span>
                                  <strong>Riders:</strong> {ride.genderPreference === 'any' ? 'anyone' : ride.genderPreference}
                                </span>
                                {ride.driver.campus && (
                                  <span>
                                    <strong>Driver's campus:</strong> {ride.driver.campus}
                                  </span>
                                )}
                                {ride.cancelledAt && (
                                  <span>
                                    <strong>Cancelled:</strong> {formatDateTime(ride.cancelledAt)}
                                    {ride.cancellationReason ? ` — ${ride.cancellationReason}` : ''}
                                  </span>
                                )}
                              </div>
                              {open.loading && <div className="loading">Loading seat requests...</div>}
                              {open.error && <div className="alert alert-error">{open.error}</div>}
                              {!open.loading && !open.error && open.bookings.length === 0 && (
                                <p className="rides-note">Nobody has asked for a seat on this ride.</p>
                              )}
                              {!open.loading && !open.error && open.bookings.length > 0 && (
                                <table className="rides-table">
                                  <thead>
                                    <tr>
                                      <th>Rider</th>
                                      <th>Status</th>
                                      <th>Requested</th>
                                      <th>Answered</th>
                                      <th>Cancelled</th>
                                      <th>Pickup → drop-off</th>
                                      <th title="Extra driving the pickup and drop-off add">Detour</th>
                                      <th>Price</th>
                                      <th title="Chat messages this rider sent on the ride">Chat</th>
                                      <th title="Times the rider asked again after cancelling">Re-requests</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {open.bookings.map((b) => (
                                      <tr key={b.id}>
                                        <td>
                                          <Link to={personPath(b.passenger.id)}>
                                            {b.passenger.fullName || `#${b.passenger.id}`}
                                          </Link>
                                          {b.passenger.email && <div className="member-email">{b.passenger.email}</div>}
                                        </td>
                                        <td>
                                          <span className={`rm-booking rm-booking-${b.status}`}>
                                            {BOOKING_STATUS_LABELS[b.status] || b.status}
                                          </span>
                                        </td>
                                        <td className="rm-nowrap">{formatDateTime(b.requestedAt)}</td>
                                        <td className="rm-nowrap">
                                          {b.respondedAt ? (
                                            <>
                                              {formatDateTime(b.respondedAt)}
                                              <div className="member-email">
                                                after{' '}
                                                {durationText(
                                                  Math.round(
                                                    (new Date(b.respondedAt).getTime() - new Date(b.requestedAt).getTime()) / 60_000
                                                  )
                                                )}
                                              </div>
                                            </>
                                          ) : (
                                            '—'
                                          )}
                                        </td>
                                        <td className="rm-nowrap">
                                          {b.cancelledAt ? (
                                            <>
                                              {formatDateTime(b.cancelledAt)}
                                              <div className="member-email">
                                                {b.cancelledBy === 'passenger'
                                                  ? 'by the rider'
                                                  : b.cancelledBy === 'driver'
                                                    ? 'by the driver'
                                                    : 'not recorded who'}
                                                {b.cancellationReason ? ` — ${b.cancellationReason}` : ''}
                                              </div>
                                            </>
                                          ) : (
                                            '—'
                                          )}
                                        </td>
                                        <td>{b.pickup || b.dropoff ? `${b.pickup || '—'} → ${b.dropoff || '—'}` : '—'}</td>
                                        <td className="numeric">{b.detourMinutes === null ? '—' : `${b.detourMinutes} min`}</td>
                                        <td className="numeric">{priceText(b.price)}</td>
                                        <td className="numeric">{b.messages}</td>
                                        <td className="numeric">{b.rebookCount}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="pagination">
              <button onClick={() => setPage(page - 1)} disabled={page === 1} className="btn btn-secondary btn-sm">
                Previous
              </button>
              <span className="pagination-info">
                Page {page} of {Math.max(1, pagination.pages)} ({pagination.total} rides)
              </span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page >= pagination.pages}
                className="btn btn-secondary btn-sm"
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RidesMonitor;
